import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Printer, ScrollText, AlertTriangle, ArrowRightCircle, ShieldAlert, Route as RouteIcon, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

/**
 * Stone Code Rules — admin-only, static/printable reference for the
 * 4-position ERP diamond-code decoder.
 *
 * Reads /api/stone-code-rules (which itself pulls live from
 * services/stone_code_decoder.py) so this page never drifts from
 * the actual decode logic.
 *
 * Print-friendly: uses `print-page` scope + a media query in the JSX
 * so users can Ctrl+P and get a clean 2-3 page reference document.
 */

const PARTITION_LABEL = {
  round_only: 'Round-only',
  fancy_only: 'Fancy-only',
  shared: 'Round + Fancy',
};

const PARTITION_STYLE = {
  round_only: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  fancy_only: 'bg-amber-50 text-amber-800 border-amber-200',
  shared: 'bg-slate-50 text-slate-700 border-slate-200',
};

const VERDICT_ICON = {
  dv_auto: ArrowRightCircle,
  csv_standard: RouteIcon,
  csv_premium: ShieldAlert,
  reject: AlertTriangle,
};

const VERDICT_STYLE = {
  dv_auto: 'border-emerald-300 bg-emerald-50',
  csv_standard: 'border-sky-300 bg-sky-50',
  csv_premium: 'border-purple-300 bg-purple-50',
  reject: 'border-rose-300 bg-rose-50',
};

const VERDICT_LABEL = {
  dv_auto: 'DV Auto-fill',
  csv_standard: 'CSV — Standard',
  csv_premium: 'CSV — Premium (Admin)',
  reject: 'Tag Rejected',
};

export default function StoneCodeRules() {
  const navigate = useNavigate();
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);

  // Try-it decoder state
  const [tryCode, setTryCode] = useState('');
  const [tryIsDiamond, setTryIsDiamond] = useState(true);
  const [tryResult, setTryResult] = useState(null);
  const [tryLoading, setTryLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/stone-code-rules/');
        setRules(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load stone code rules');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const runTryDecode = async (codeOverride) => {
    const code = (codeOverride ?? tryCode).trim();
    if (!code) {
      toast.error('Enter a stone code first');
      return;
    }
    setTryLoading(true);
    try {
      const r = await api.post('/stone-code-rules/try-decode', {
        stone_code: code,
        is_diamond: tryIsDiamond,
      });
      setTryResult(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Decode failed';
      setTryResult(null);
      toast.error(msg);
    } finally {
      setTryLoading(false);
    }
  };

  const runSample = (code) => {
    setTryCode(code);
    runTryDecode(code);
  };

  if (loading) {
    return <div className="p-8 text-slate-500">Loading rules…</div>;
  }
  if (!rules) {
    return <div className="p-8 text-slate-500">No rules available.</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 print:bg-white" data-testid="stone-code-rules-page">
      {/* Print CSS scope: hide chrome, force serif-ish printable colours */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .print-page { break-inside: avoid; }
          .print-page + .print-page { page-break-before: auto; }
          table { page-break-inside: avoid; }
          h2 { page-break-after: avoid; }
        }
      `}</style>

      {/* Header (screen only) */}
      <div className="no-print sticky top-0 z-10 bg-white border-b border-slate-200 px-4 sm:px-8 py-3 flex items-center gap-3 shadow-sm">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 text-sm"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex-1">
          <h1 className="text-base sm:text-lg font-semibold text-slate-900 flex items-center gap-2">
            <ScrollText className="w-5 h-5 text-slate-700" />
            Stone Code Rules — Diamond Verification
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            ERP ↔ DV 4-position mapping · admin-only reference
          </p>
        </div>
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm bg-slate-900 text-white hover:bg-slate-800"
          data-testid="print-btn"
        >
          <Printer className="w-4 h-4" /> Print
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-6 space-y-8">
        {/* ─── Anatomy diagram ─────────────────────────────────────────── */}
        <section className="print-page bg-white rounded-lg border border-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
            Anatomy of a stone code
          </h2>
          <div className="flex flex-wrap items-stretch gap-2 sm:gap-3">
            {[
              { label: 'Prefix', value: 'C', hint: 'Optional (C=YL, F=FC)', tone: 'bg-slate-100 text-slate-700' },
              { label: 'Pos 1', value: '3', hint: 'Colour → G, H', tone: 'bg-blue-50 text-blue-800' },
              { label: 'Pos 2', value: '2', hint: 'Clarity → VS', tone: 'bg-emerald-50 text-emerald-800' },
              { label: 'Pos 3', value: '1', hint: 'Shape → Round', tone: 'bg-amber-50 text-amber-800' },
              { label: 'Pos 4', value: 'B', hint: 'Sieve → 0-6', tone: 'bg-rose-50 text-rose-800' },
            ].map((cell) => (
              <div key={cell.label} className={`flex-1 min-w-[110px] rounded-md border border-slate-200 p-3 ${cell.tone}`}>
                <div className="text-[10px] font-medium uppercase tracking-wider opacity-80">{cell.label}</div>
                <div className="text-2xl font-bold font-mono mt-1">{cell.value}</div>
                <div className="text-[11px] mt-1 opacity-90">{cell.hint}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-3">
            Example decoded: <span className="font-mono font-semibold text-slate-800">C321B</span> → Colour <b>G, H</b> · Clarity <b>VS</b> · Shape <b>Round</b> · Size <b>0-6</b> · Prefix <b>C</b> (YL variant, no DV effect).
          </p>
        </section>

        {/* ─── Try-it decoder (screen only) ────────────────────────────── */}
        <section className="no-print bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-lg p-5 shadow-lg" data-testid="try-it-section">
          <h2 className="text-sm font-semibold uppercase tracking-wide mb-3 flex items-center gap-2 text-emerald-300">
            <Wand2 className="w-4 h-4" />
            Try it — paste any ERP code
          </h2>
          <div className="flex flex-col sm:flex-row gap-2 items-stretch">
            <input
              type="text"
              value={tryCode}
              onChange={(e) => setTryCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter') runTryDecode(); }}
              placeholder="e.g. 321B, C521C, U71A, C.DIA, RD100D"
              className="flex-1 px-3 py-2 rounded-md bg-slate-950/60 border border-slate-700 text-white placeholder:text-slate-500 font-mono uppercase focus:outline-none focus:ring-2 focus:ring-emerald-400"
              data-testid="try-code-input"
              autoFocus
            />
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-950/40 border border-slate-700 text-xs cursor-pointer select-none">
              <input
                type="checkbox"
                checked={tryIsDiamond}
                onChange={(e) => setTryIsDiamond(e.target.checked)}
                className="accent-emerald-400"
                data-testid="try-is-diamond"
              />
              ERP <span className="font-mono">is_diamond</span>
            </label>
            <button
              onClick={() => runTryDecode()}
              disabled={tryLoading}
              className="px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold text-sm disabled:opacity-50"
              data-testid="try-decode-btn"
            >
              {tryLoading ? 'Decoding…' : 'Decode'}
            </button>
          </div>

          <div className="flex flex-wrap gap-1.5 mt-3 text-[11px]">
            <span className="text-slate-400 mr-1">Samples:</span>
            {['321B', 'C521C', 'U71A', '371F', 'C.DIA', 'RD100D', '321M', 'XYZ99'].map((s) => (
              <button
                key={s}
                onClick={() => runSample(s)}
                className="px-2 py-0.5 rounded bg-slate-700 hover:bg-slate-600 font-mono"
                data-testid={`try-sample-${s}`}
              >
                {s}
              </button>
            ))}
          </div>

          {tryResult && (
            <div
              className={`mt-4 rounded-md p-4 border ${
                tryResult.verdict === 'dv_auto' ? 'border-emerald-400/60 bg-emerald-500/10' :
                tryResult.verdict === 'csv_standard' ? 'border-sky-400/60 bg-sky-500/10' :
                tryResult.verdict === 'csv_premium' ? 'border-purple-400/60 bg-purple-500/10' :
                'border-rose-400/60 bg-rose-500/10'
              }`}
              data-testid="try-result"
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="font-mono text-lg font-bold">{tryResult.input}</span>
                <span className="text-slate-400 text-xs">→</span>
                <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide ${
                  tryResult.verdict === 'dv_auto' ? 'bg-emerald-400 text-slate-900' :
                  tryResult.verdict === 'csv_standard' ? 'bg-sky-400 text-slate-900' :
                  tryResult.verdict === 'csv_premium' ? 'bg-purple-400 text-slate-900' :
                  'bg-rose-400 text-slate-900'
                }`} data-testid="try-verdict-badge">
                  {VERDICT_LABEL[tryResult.verdict]}
                </span>
                {tryResult.pricing_mode && (
                  <span className="text-[10px] text-slate-400 font-mono">pricing: {tryResult.pricing_mode}</span>
                )}
              </div>
              <div className="text-sm text-slate-200 mb-3">{tryResult.reason}</div>

              {tryResult.decoded ? (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                  <div className="rounded bg-slate-950/50 p-2">
                    <div className="text-[10px] uppercase text-slate-400">Prefix</div>
                    <div className="font-mono font-semibold mt-0.5">{tryResult.decoded.prefix || '—'}</div>
                  </div>
                  <div className="rounded bg-slate-950/50 p-2">
                    <div className="text-[10px] uppercase text-slate-400">Colour</div>
                    <div className="font-mono font-semibold mt-0.5">{tryResult.decoded.colours.join(', ')}</div>
                  </div>
                  <div className="rounded bg-slate-950/50 p-2">
                    <div className="text-[10px] uppercase text-slate-400">Clarity</div>
                    <div className="font-mono font-semibold mt-0.5">
                      {tryResult.decoded.clarity}
                      {tryResult.decoded.auto_csv && <span className="ml-1 text-amber-300 text-[10px]">(→CSV)</span>}
                    </div>
                  </div>
                  <div className="rounded bg-slate-950/50 p-2">
                    <div className="text-[10px] uppercase text-slate-400">Shape</div>
                    <div className="font-mono font-semibold mt-0.5">{tryResult.decoded.shape}</div>
                  </div>
                  <div className="rounded bg-slate-950/50 p-2">
                    <div className="text-[10px] uppercase text-slate-400">Size chip(s)</div>
                    <div className="font-mono font-semibold mt-0.5">{tryResult.decoded.sizes.join(' & ')}</div>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-400 italic">No 4-position decode (handled by literal / GIA / reject rule above).</div>
              )}
            </div>
          )}
        </section>

        {/* ─── Prefix ──────────────────────────────────────────────────── */}
        <section className="print-page bg-white rounded-lg border border-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
            Optional Prefix (variant only)
          </h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4 w-28">Prefix</th>
                <th className="py-2">Meaning</th>
              </tr>
            </thead>
            <tbody>
              {rules.prefixes.map((row) => (
                <tr key={row.prefix} className="border-b border-slate-100">
                  <td className="py-2 pr-4 font-mono font-semibold text-slate-800">{row.prefix}</td>
                  <td className="py-2 text-slate-700">{row.meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* ─── Position 1 — Colour ─────────────────────────────────────── */}
        <section className="print-page bg-white rounded-lg border border-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
            Position 1 — Colour
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {rules.position_1_colour.map((row) => (
              <div key={row.code} className="border border-slate-200 rounded-md p-2 bg-blue-50/40">
                <div className="text-lg font-bold font-mono text-blue-900">{row.code}</div>
                <div className="text-xs text-slate-600 mt-0.5">→ {row.grades.join(', ')}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ─── Position 2 — Clarity ────────────────────────────────────── */}
        <section className="print-page bg-white rounded-lg border border-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
            Position 2 — Clarity
          </h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4 w-20">Code</th>
                <th className="py-2 pr-4 w-24">DV Chip</th>
                <th className="py-2">Note</th>
              </tr>
            </thead>
            <tbody>
              {rules.position_2_clarity.map((row) => (
                <tr key={row.code} className={`border-b border-slate-100 ${row.code === '7' ? 'bg-amber-50/60' : ''}`}>
                  <td className="py-2 pr-4 font-mono font-semibold text-slate-800">{row.code}</td>
                  <td className="py-2 pr-4">
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 text-xs font-semibold">
                      {row.chip}
                    </span>
                  </td>
                  <td className="py-2 text-slate-600 text-xs">{row.note || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* ─── Position 3 — Shape ──────────────────────────────────────── */}
        <section className="print-page bg-white rounded-lg border border-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
            Position 3 — Shape
          </h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4 w-56">Code(s)</th>
                <th className="py-2">Shape</th>
              </tr>
            </thead>
            <tbody>
              {rules.position_3_shape.map((row) => (
                <tr key={row.shape} className="border-b border-slate-100">
                  <td className="py-2 pr-4 font-mono font-semibold text-slate-800">{row.code}</td>
                  <td className="py-2 text-slate-700">{row.shape}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* ─── Position 4 — Sieve ──────────────────────────────────────── */}
        <section className="print-page bg-white rounded-lg border border-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
            Position 4 — Sieve letter → DV size chip
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3 w-20">Letter</th>
                  <th className="py-2 pr-3 w-24">Cents</th>
                  <th className="py-2 pr-3">Round chip</th>
                  <th className="py-2 pr-3">Fancy chip</th>
                  <th className="py-2 w-32">Availability</th>
                </tr>
              </thead>
              <tbody>
                {rules.position_4_sieve.map((row) => {
                  const fancy = row.fancy_chip;
                  const fancyDisplay = Array.isArray(fancy)
                    ? fancy.join('  &  ')
                    : (fancy || '—');
                  return (
                    <tr key={row.letter} className="border-b border-slate-100">
                      <td className="py-2 pr-3 font-mono font-semibold text-slate-800">{row.letter}</td>
                      <td className="py-2 pr-3 text-slate-700">{row.cents}</td>
                      <td className="py-2 pr-3">
                        {row.round_chip ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 text-xs font-semibold font-mono">
                            {row.round_chip}
                          </span>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="py-2 pr-3">
                        {fancy ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-amber-50 text-amber-800 text-xs font-semibold font-mono">
                            {fancyDisplay}
                          </span>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[10px] font-medium ${PARTITION_STYLE[row.partition]}`}>
                          {PARTITION_LABEL[row.partition]}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-3 leading-relaxed">
            <b>Cross-pollination guard:</b> a Round-only letter under a Fancy code (or vice-versa) is REJECTED,
            not decoded. E.g. <span className="font-mono">321M</span> — Round shape + Fancy-only sieve <b>M</b> — is rejected.
            <br />
            <b>Fancy 0-6c special case:</b> letters M/N/O/P/Q pre-select <b>both</b> <span className="font-mono">-7</span> and <span className="font-mono">+7</span> chips together.
          </p>
        </section>

        {/* ─── Routing verdicts ────────────────────────────────────────── */}
        <section className="print-page bg-white rounded-lg border border-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
            Routing verdicts
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {rules.verdicts.map((v) => {
              const Icon = VERDICT_ICON[v.verdict] || RouteIcon;
              return (
                <div key={v.verdict} className={`rounded-md border p-3 ${VERDICT_STYLE[v.verdict]}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4 text-slate-700" />
                    <div className="font-semibold text-sm text-slate-900">{VERDICT_LABEL[v.verdict]}</div>
                    <code className="ml-auto text-[10px] text-slate-500">{v.verdict}</code>
                  </div>
                  <div className="text-xs text-slate-700"><b>Trigger:</b> {v.trigger}</div>
                  <div className="text-xs text-slate-700 mt-1"><b>Outcome:</b> {v.outcome}</div>
                  <div className="text-xs text-slate-600 mt-1"><b>Example:</b> <span className="font-mono">{v.example}</span></div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ─── Gotchas ─────────────────────────────────────────────────── */}
        <section className="print-page bg-white rounded-lg border border-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            Gotchas — read at least once
          </h2>
          <ol className="list-decimal pl-5 space-y-1.5 text-sm text-slate-700">
            {rules.gotchas.map((g, i) => (
              <li key={i}>{g}</li>
            ))}
          </ol>
        </section>

        <p className="text-[11px] text-slate-400 text-center pb-4">
          Source of truth: <code>backend/services/stone_code_decoder.py</code> · Any rule change here reflects on next page load.
        </p>
      </div>
    </div>
  );
}
